/*
 * Approving: the moment the pharmacy's drafts reach the home's chart.
 *
 * It is the one check on screen, so it shows everything rather than a count: every draft, what it
 * replaces beside what it becomes, every warning — each ticked as read before Approve will work —
 * and the reports from the home that this deals with. For one resident, or for the whole home when
 * a cycle has been set up. What is approved is exactly what is listed here: anything added after
 * this opened stays a draft, and if anything listed has changed, nothing is approved and it says so.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApiError, api } from './api.ts';
import { shortDay } from './days.ts';
import type { Draft, Medicine, Report } from './medicine-types.ts';
import { Overlay } from './overlay.tsx';

type Props = {
  homeId: string;
  residentId?: string;
  onClose: () => void;
  onDone: (approved: number) => void;
};

export function ApprovalDialog({ homeId, residentId, onClose, onDone }: Props) {
  const [drafts, setDrafts] = useState<Draft[] | null>(null);
  // Residents whose seals are out of date, from the server with the drafts.
  const [reprintNeeded, setReprintNeeded] = useState<string[]>([]);
  const [reports, setReports] = useState<Report[]>([]);
  const [read, setRead] = useState<Set<string>>(new Set());
  const [closing, setClosing] = useState<Set<string>>(new Set());
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const one = residentId ? `?residentId=${residentId}` : '';
      const [draftData, reportData] = await Promise.all([
        api.get<{ drafts: Draft[]; reprintNeeded: string[] }>(`/api/homes/${homeId}/drafts${one}`),
        api.get<{ reports: Report[] }>(`/api/homes/${homeId}/reports?show=open`),
      ]);
      const residents = new Set(draftData.drafts.map((d) => d.residentId));
      const relevant = reportData.reports.filter((r) => residents.has(r.residentId));
      setDrafts(draftData.drafts);
      setReprintNeeded(draftData.reprintNeeded ?? []);
      setReports(relevant);
      setRead(new Set());
      // A report is ticked as dealt with when a draft touches the same medicine, or it reported a
      // new medicine and a new one is being added. The approver can untick either.
      setClosing(new Set(relevant.filter((r) => draftData.drafts.some((d) => dealsWith(d, r))).map((r) => r.id)));
      setError('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the drafts.');
    }
  }, [homeId, residentId]);

  useEffect(() => {
    void load();
  }, [load]);

  const byResident = useMemo(() => {
    const groups = new Map<string, { name: string; drafts: Draft[] }>();
    for (const draft of drafts ?? []) {
      const group = groups.get(draft.residentId) ?? { name: draft.residentName, drafts: [] };
      group.drafts.push(draft);
      groups.set(draft.residentId, group);
    }
    return [...groups.entries()];
  }, [drafts]);

  const needTick = (drafts ?? []).filter((d) => d.allergyWarning || d.clashWarning);
  const allRead = needTick.every((d) => read.has(d.id));
  const staleSeals = byResident.filter(([id]) => reprintNeeded.includes(id));

  const approve = async () => {
    if (!drafts?.length) return;
    setBusy(true);
    setError('');
    try {
      const result = await api.post<{ approved: number }>(`/api/homes/${homeId}/approve`, {
        changeIds: drafts.map((d) => d.id),
        acknowledged: [...read],
        closeReports: [...closing],
      });
      onDone(result.approved);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Nothing was approved.');
      // If something moved underneath us, show what is there now.
      if (err instanceof ApiError && err.status === 409) void load();
      setBusy(false);
    }
  };

  const toggle = (set: Set<string>, id: string) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  };

  return (
    <Overlay onClose={onClose}>
      <div className="card wide approval">
        <h2>
          {residentId
            ? `Approve ${byResident[0]?.[1].name ? `${byResident[0][1].name}'s` : 'their'} changes`
            : 'Approve everything for this home'}
        </h2>
        {drafts === null ? <p className="muted">Loading…</p> : null}
        {drafts && !drafts.length ? <p className="muted">There is nothing waiting to be approved.</p> : null}

        {drafts?.length ? (
          <p className="muted">
            {drafts.length} change{drafts.length === 1 ? '' : 's'}
            {byResident.length > 1 ? ` for ${byResident.length} residents` : ''}. Once approved, the home sees them on
            the chart and the round from the date each one starts.
          </p>
        ) : null}

        {staleSeals.length ? (
          <p className="notice warn">
            Seals out of date for {staleSeals.map(([, g]) => g.name).join(', ')}: their medicines changed after the
            seals were printed. Reprint before these trays go out.
          </p>
        ) : null}

        {byResident.map(([id, group]) => (
          <section key={id} className="approval-resident">
            {byResident.length > 1 || !residentId ? <h3>{group.name}</h3> : null}
            <ul className="list">
              {group.drafts.map((draft) => (
                <li key={draft.id}>
                  <DraftLine draft={draft} />
                  {draft.allergyWarning || draft.clashWarning ? (
                    <label className="check warn-tick" htmlFor={`read-${draft.id}`}>
                      <input
                        id={`read-${draft.id}`}
                        type="checkbox"
                        checked={read.has(draft.id)}
                        onChange={() => setRead((set) => toggle(set, draft.id))}
                      />
                      <span>
                        {draft.allergyWarning ? <strong className="warn-text">{draft.allergyWarning}</strong> : null}
                        {draft.clashWarning ? <strong className="warn-text">{draft.clashWarning}</strong> : null}
                        <span className="small"> I have read this and it is right to go ahead.</span>
                      </span>
                    </label>
                  ) : null}
                </li>
              ))}
            </ul>
          </section>
        ))}

        {reports.length ? (
          <fieldset>
            <legend>What the home reported</legend>
            <p className="muted small">Tick the ones this approval deals with. They are marked done and linked to it.</p>
            {reports.map((report) => (
              <label className="check" key={report.id} htmlFor={`close-${report.id}`}>
                <input
                  id={`close-${report.id}`}
                  type="checkbox"
                  checked={closing.has(report.id)}
                  onChange={() => setClosing((set) => toggle(set, report.id))}
                />
                <span>
                  <strong>{report.residentName}: {report.whatLabel}{report.medicineName ? `, ${report.medicineName}` : ''}</strong>
                  <span className="small"> — "{report.detail}"</span>
                  <span className="muted small"> {report.reportedBy}, {shortDay(report.reportedAt.slice(0, 10))}</span>
                </span>
              </label>
            ))}
          </fieldset>
        ) : null}

        {error ? <p className="error" role="alert">{error}</p> : null}

        <div className="row-actions">
          <button type="button" disabled={busy || !drafts?.length || !allRead} onClick={() => void approve()}>
            {busy ? 'Approving…' : `Approve ${drafts?.length ?? 0} change${drafts?.length === 1 ? '' : 's'}`}
          </button>
          <button type="button" className="quiet" onClick={onClose} disabled={busy}>Not yet</button>
        </div>
        {!allRead && needTick.length ? (
          <p className="muted small">Tick each warning to say you have read it.</p>
        ) : null}
      </div>
    </Overlay>
  );
}

function dealsWith(draft: Draft, report: Report): boolean {
  if (draft.residentId !== report.residentId) return false;
  if (report.medicineId && draft.medicineId === report.medicineId) return true;
  return report.what === 'new' && draft.kind === 'new';
}

/* One draft, read the way a pharmacist would read it: what it was, what it will be, from when. */
export function DraftLine({ draft }: { draft: Draft }) {
  const when = draft.startsOn ? shortDay(draft.startsOn) : 'straight away';
  if (draft.kind === 'stop') {
    return (
      <div className="row static column draft-line">
        <div className="row-line">
          <span><span className="chip off">Stopping</span> <span className="row-main">{draft.medicineName || draft.before?.name}</span></span>
          <span className="muted small">from {when}</span>
        </div>
        {draft.before ? <p className="muted small">Now: {draft.before.directions}</p> : null}
        <p className="small">Why: {draft.stopReason}</p>
        <Source draft={draft} />
      </div>
    );
  }
  return (
    <div className="row static column draft-line">
      <div className="row-line">
        <span>
          <span className={`chip ${draft.kind === 'new' ? 'done' : 'warn'}`}>{draft.kind === 'new' ? 'Adding' : 'Changing'}</span>{' '}
          <span className="row-main">{draft.name}</span>
        </span>
        <span className="muted small">
          from {when}{draft.endsOn ? `, last day ${shortDay(draft.endsOn)}` : ''}
        </span>
      </div>
      {draft.kind === 'change' && draft.before ? <Compare before={draft.before} after={draft} /> : (
        <p className="small">{draft.directions}{draft.schedule.length ? ` · ${draft.schedule.join('; ')}` : ''}</p>
      )}
      <Traits item={draft} />
      <Source draft={draft} />
    </div>
  );
}

function Compare({ before, after }: { before: Medicine; after: Draft }) {
  return (
    <dl className="compare">
      <div><dt>Now</dt><dd>{before.name} — {before.directions}{before.schedule.length ? ` · ${before.schedule.join('; ')}` : ''}</dd></div>
      <div><dt>After</dt><dd>{after.name} — {after.directions}{after.schedule.length ? ` · ${after.schedule.join('; ')}` : ''}</dd></div>
    </dl>
  );
}

export function Traits({ item }: { item: Pick<Medicine, 'prn' | 'controlledDrug' | 'timeCritical' | 'nurseOnly' | 'trayType' | 'flags'> }) {
  return (
    <span className="chips">
      {item.prn ? <span className="chip">When required</span> : null}
      {item.controlledDrug ? <span className="chip warn">Controlled drug</span> : null}
      {item.timeCritical ? <span className="chip warn">Time critical</span> : null}
      {item.nurseOnly ? <span className="chip">Nurses only</span> : null}
      {item.flags?.insulin ? <span className="chip">Insulin</span> : null}
      {item.flags?.warfarin ? <span className="chip">Warfarin</span> : null}
      {item.flags?.peg ? <span className="chip">PEG</span> : null}
      {item.flags?.patch ? <span className="chip">Patch</span> : null}
      <span className="chip">{item.trayType === 'tray' ? 'In the tray' : item.trayType === 'separate' ? 'Tray of its own' : 'Original pack'}</span>
    </span>
  );
}

function Source({ draft }: { draft: Draft }) {
  if (!draft.sourceDetail && !draft.note) return null;
  return (
    <p className="muted small">
      {draft.sourceDetail ? `Source: ${draft.sourceDetail}` : ''}
      {draft.sourceDetail && draft.note ? ' · ' : ''}
      {draft.note ? `Note: ${draft.note}` : ''}
      {draft.proposedByName ? ` · entered by ${draft.proposedByName}` : ''}
    </p>
  );
}
